import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { XIcon } from "../icons";
import useDoctorNotifications from "../../hooks/useDoctorNotifications";
import { ROUTES } from "../../routes";
import NotificationCard from "./NotificationCard";

import "./doctor-notifications.css";

const DOCK_PREVIEW_LIMIT = 5;

/**
 * Постоянный dock: свёрнутый таб + панель со списком (не закрывается кликом снаружи).
 */
export default function NotificationsDock() {
    const navigate = useNavigate();
    const { enabled, notifications, unreadTotal, markRead, markAllRead } = useDoctorNotifications();
    const [expanded, setExpanded] = useState(false);
    const [markingId, setMarkingId] = useState(null);
    const [markingAll, setMarkingAll] = useState(false);

    const hasCritical = notifications.some((a) => a.situation_class === "S4");

    const handleMarkRead = useCallback(
        async (alert) => {
            setMarkingId(alert.id);
            try {
                await markRead(alert.id);
            } finally {
                setMarkingId(null);
            }
        },
        [markRead]
    );

    const handleMarkAllRead = useCallback(async () => {
        setMarkingAll(true);
        try {
            await markAllRead();
        } finally {
            setMarkingAll(false);
        }
    }, [markAllRead]);

    const handleOpenPatient = useCallback(
        (alert) => {
            navigate(ROUTES.PATIENTS, {
                state: { highlightPatientId: alert.patient_id },
                viewTransition: true,
            });
        },
        [navigate]
    );

    if (!enabled || unreadTotal <= 0) return null;

    const preview = notifications.slice(0, DOCK_PREVIEW_LIMIT);

    return (
        <div
            className={`doctor-notifications-dock ${expanded ? "doctor-notifications-dock--expanded" : ""} ${hasCritical ? "doctor-notifications-dock--critical" : ""}`}
        >
            {expanded && (
                <div
                    className="doctor-notifications-dock__panel"
                    role="dialog"
                    aria-label="Непрочитанные уведомления"
                >
                    <div className="doctor-notifications-dock__panel-header">
                        <h2 className="doctor-notifications-dock__panel-title">Уведомления</h2>
                        <button
                            type="button"
                            className="doctor-notifications-dock__close"
                            aria-label="Свернуть панель"
                            onClick={() => setExpanded(false)}
                        >
                            <XIcon size={18} aria-hidden />
                        </button>
                    </div>
                    <ul className="doctor-notifications-dock__list">
                        {preview.map((alert) => (
                            <li key={alert.id}>
                                <NotificationCard
                                    alert={alert}
                                    compact
                                    markingRead={markingId === alert.id}
                                    onMarkRead={handleMarkRead}
                                    onOpenPatient={handleOpenPatient}
                                />
                            </li>
                        ))}
                    </ul>
                    {notifications.length > DOCK_PREVIEW_LIMIT && (
                        <p className="doctor-notifications-dock__more">
                            и ещё {notifications.length - DOCK_PREVIEW_LIMIT}…
                        </p>
                    )}
                    <div style={{ display: "flex", gap: "8px", marginTop: "12px", width: "100%" }}>
                        <button
                            type="button"
                            className="doctor-notification-btn doctor-notification-btn--ghost"
                            style={{ flex: 1, padding: "8px 12px", fontSize: "13px" }}
                            disabled={markingAll}
                            onClick={handleMarkAllRead}
                        >
                            {markingAll ? "..." : "Прочитать все"}
                        </button>
                        <button
                            type="button"
                            className="doctor-notification-btn doctor-notification-btn--primary"
                            style={{ flex: 1, padding: "8px 12px", fontSize: "13px" }}
                            onClick={() => {
                                setExpanded(false);
                                navigate(ROUTES.NOTIFICATIONS, { viewTransition: true });
                            }}
                        >
                            Все →
                        </button>
                    </div>
                </div>
            )}

            <button
                type="button"
                className="doctor-notifications-dock__tab"
                aria-expanded={expanded}
                aria-label={`Уведомления, непрочитанных: ${unreadTotal}`}
                onClick={() => setExpanded((v) => !v)}
            >
                <span className="doctor-notifications-dock__tab-icon" aria-hidden="true">
                    ⚠
                </span>
                <span className="doctor-notifications-dock__tab-count">{unreadTotal > 99 ? "99+" : unreadTotal}</span>
            </button>
        </div>
    );
}
